/**
 * State set to a new array or object renders, even when it holds what the old one held.
 * These give back the previous value when nothing differs, so such an update renders nothing.
 */

/** `next`, or `previous` itself when both hold the same items in the same order. */
export const keepList = <T>(previous: T[], next: T[], same: (a: T, b: T) => boolean = Object.is): T[] => {
    if (previous === next) return previous;
    if (previous.length !== next.length) return next;
    for (let i = 0; i < next.length; i++) {
        if (!same(previous[i], next[i])) return next;
    }
    return previous;
};

/** Whether two plain values (what JSON can hold) are the same all the way down. A key holding undefined counts as absent. */
export const sameJson = (a: unknown, b: unknown): boolean => {
    if (a === b) return true;
    if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
    if (Array.isArray(a) !== Array.isArray(b)) return false;
    if (Array.isArray(a)) {
        const other = b as unknown[];
        if (a.length !== other.length) return false;
        for (let i = 0; i < a.length; i++) {
            if (!sameJson(a[i], other[i])) return false;
        }
        return true;
    }
    const mine = a as Record<string, unknown>;
    const theirs = b as Record<string, unknown>;
    let defined = 0;
    for (const key of Object.keys(mine)) {
        if (mine[key] === undefined) continue;
        defined++;
        if (!sameJson(mine[key], theirs[key])) return false;
    }
    for (const key of Object.keys(theirs)) {
        if (theirs[key] !== undefined) defined--;
    }
    return defined === 0;
};
